// Helper functions for calculating streaks

import { getDayCoordinates, getDayKey } from "./roadmapHelpers";

export interface StreakData {
  currentStreak: number;
  longestStreak: number;
  lastCompletedDay: number | null;
}

export interface StreakBadge {
  days: number;
  title: string;
  description: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
}

export const streakBadges: StreakBadge[] = [
  {
    days: 7,
    title: "Week Warrior",
    description: "Completed Week 1! 🔥",
    icon: "🔥",
    color: "text-orange-600 dark:text-orange-400",
    bgColor: "bg-orange-500/10",
    borderColor: "border-orange-500/50",
  },
  {
    days: 14,
    title: "Fortnight Fighter",
    description: "Two weeks strong! 💪",
    icon: "💪",
    color: "text-purple-600 dark:text-purple-400",
    bgColor: "bg-purple-500/10",
    borderColor: "border-purple-500/50",
  },
  {
    days: 21,
    title: "Three-Week Champion",
    description: "Three weeks conquered! ⭐",
    icon: "⭐",
    color: "text-blue-600 dark:text-blue-400",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/50",
  },
  {
    days: 28,
    title: "Legendary Learner",
    description: "All 28 days complete! 🏆",
    icon: "🏆",
    color: "text-yellow-600 dark:text-yellow-400",
    bgColor: "bg-yellow-500/10",
    borderColor: "border-yellow-500/50",
  },
];

/**
 * Calculate current streak from completed tasks
 * A streak is consecutive days completed starting from day 1
 */
export function calculateStreak(
  completedTasks: { [key: string]: boolean },
  totalDays: number
): StreakData {
  let currentStreak = 0;
  let longestStreak = 0;
  let tempStreak = 0;
  let lastCompletedDay: number | null = null;

  // Check each day sequentially
  for (let dayNum = 1; dayNum <= totalDays; dayNum++) {
    const { week, day } = getDayCoordinates(dayNum);
    const dayKey = getDayKey(week, day);
    const isCompleted = completedTasks[dayKey];

    if (isCompleted) {
      tempStreak++;
      lastCompletedDay = dayNum;
      
      // Update longest streak
      if (tempStreak > longestStreak) {
        longestStreak = tempStreak;
      }
      
      // Update current streak only if this is consecutive from start
      if (currentStreak === dayNum - 1) {
        currentStreak = tempStreak;
      }
    } else {
      // Break in streak
      tempStreak = 0;
    }
  }

  return {
    currentStreak,
    longestStreak,
    lastCompletedDay,
  };
}

/**
 * Get earned badges based on current streak
 */
export function getEarnedBadges(currentStreak: number): StreakBadge[] {
  return streakBadges.filter(badge => currentStreak >= badge.days);
}

/**
 * Get next badge to achieve
 */
export function getNextBadge(currentStreak: number): StreakBadge | null {
  return streakBadges.find(badge => currentStreak < badge.days) || null;
}

/**
 * Calculate progress to next badge
 */
export function getNextBadgeProgress(currentStreak: number): {
  badge: StreakBadge | null;
  progress: number;
  daysRemaining: number;
} {
  const nextBadge = getNextBadge(currentStreak);
  
  if (!nextBadge) {
    return { badge: null, progress: 100, daysRemaining: 0 };
  }

  const progress = (currentStreak / nextBadge.days) * 100;
  const daysRemaining = nextBadge.days - currentStreak;

  return { badge: nextBadge, progress, daysRemaining };
}
